/**
 * In-memory synergy adjacency for custom-blend navigation.
 * Built once from the ingredients payload — O(1) lookups on select.
 *
 * Ranking = consensus votes, then diversified across capacity buckets
 * (cup body / partner / accent) so suggestions help complete a blend.
 * Prep alignment: only IDs in eligibleIdSet (prep-compatible) can be suggested.
 */

import { capacityBucket, diversifyByBucket } from './synergyBuckets';

export { capacityBucket, diversifyByBucket, BUCKET_ORDER } from './synergyBuckets';

export const TIP_NAME_CAP = 5;
export const HIGHLIGHT_CAP = 5;
/** Hide the soft tip once the blend has this many ingredients. */
export const TIP_HIDE_AT_SELECTION_COUNT = 10;

function familyKeyFor(ing) {
  return ing.variant_family || ing.id;
}

function stripOrganicLabel(name) {
  const base = String(name || '').replace(/_/g, ' ').trim();
  if (!base) return '';
  const lower = base.toLowerCase();
  if (lower.startsWith('organic ')) return base.slice(8).trim();
  if (lower.endsWith('(organic)')) {
    return base.slice(0, -9).trim();
  }
  return base;
}

function tierRank(tier) {
  if (tier === 'full') return 4;
  if (tier === 'high') return 3;
  if (tier === 'mid') return 2;
  return 1;
}

/**
 * @param {Array<object>} ingredients flat API ingredient list
 */
export function buildSynergyIndex(ingredients) {
  const partnersById = new Map();
  const familyKeyById = new Map();
  const displayNameByFamily = new Map();
  const inStockByFamily = new Map();
  const typeByFamily = new Map();
  const capacityTierByFamily = new Map();

  for (const ing of ingredients) {
    const familyKey = familyKeyFor(ing);
    familyKeyById.set(ing.id, familyKey);

    const label = stripOrganicLabel(ing.name);
    if (ing.quality === 'standard' || !displayNameByFamily.has(familyKey)) {
      displayNameByFamily.set(familyKey, label);
    }

    inStockByFamily.set(
      familyKey,
      Boolean(inStockByFamily.get(familyKey)) || Boolean(ing.in_stock),
    );

    if (!typeByFamily.has(familyKey)) {
      typeByFamily.set(familyKey, ing.type);
    }

    const tier = ing.capacity_tier || 'low';
    const prev = capacityTierByFamily.get(familyKey);
    if (!prev || tierRank(tier) > tierRank(prev)) {
      capacityTierByFamily.set(familyKey, tier);
    }

    partnersById.set(
      ing.id,
      Array.isArray(ing.synergistic_with) ? [...ing.synergistic_with] : [],
    );
  }

  return {
    partnersById,
    familyKeyById,
    displayNameByFamily,
    inStockByFamily,
    typeByFamily,
    capacityTierByFamily,
  };
}

function compareRanked(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  if (a.order !== b.order) return a.order - b.order;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

/**
 * Consensus-rank pairing partners, then diversify across capacity buckets.
 *
 * @param {object} args
 * @param {string[]} args.selectedIds
 * @param {ReturnType<typeof buildSynergyIndex>} args.index
 * @param {Set<string>} args.eligibleIdSet prep-compatible catalog IDs only
 * @param {number} [args.max]
 * @returns {{ familyKey: string, name: string, score: number, bucket: string }[]}
 */
export function rankSynergySuggestions({
  selectedIds,
  index,
  eligibleIdSet,
  max = HIGHLIGHT_CAP,
}) {
  if (!selectedIds.length || !index) return [];

  const selectedFamilies = new Set();
  const selectedBuckets = new Set();
  for (const id of selectedIds) {
    const fk = index.familyKeyById.get(id);
    if (!fk) continue;
    selectedFamilies.add(fk);
    selectedBuckets.add(capacityBucket(index.capacityTierByFamily.get(fk)));
  }

  const eligibleFamilies = new Set();
  for (const id of eligibleIdSet) {
    const fk = index.familyKeyById.get(id);
    if (!fk || selectedFamilies.has(fk)) continue;
    if (!index.inStockByFamily.get(fk)) continue;
    eligibleFamilies.add(fk);
  }

  const scores = new Map();
  const firstSeen = new Map();
  let order = 0;

  for (const id of selectedIds) {
    const partners = index.partnersById.get(id) || [];
    for (const partnerId of partners) {
      const familyKey = index.familyKeyById.get(partnerId);
      if (!familyKey || !eligibleFamilies.has(familyKey)) continue;
      scores.set(familyKey, (scores.get(familyKey) || 0) + 1);
      if (!firstSeen.has(familyKey)) {
        firstSeen.set(familyKey, order);
        order += 1;
      }
    }
  }

  const ranked = [...scores.entries()]
    .map(([familyKey, score]) => {
      const tier = index.capacityTierByFamily.get(familyKey) || 'low';
      return {
        familyKey,
        name: index.displayNameByFamily.get(familyKey) || familyKey,
        score,
        order: firstSeen.get(familyKey) ?? 0,
        bucket: capacityBucket(tier),
      };
    })
    .sort(compareRanked);

  return diversifyByBucket(ranked, selectedBuckets, max);
}

/**
 * Oxford-comma list for tip copy.
 * @param {string[]} names
 */
export function formatSynergyNameList(names) {
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * @param {number} selectedCount
 * @param {string|null} singleName
 */
export function synergyTipSubject(selectedCount, singleName) {
  if (selectedCount === 1 && singleName) return singleName;
  return 'Your blend';
}
